import { useState } from 'react';
import { toast } from 'react-toastify';
import { TOAST } from '../constants/labels';

/**
 * useCancelConfirm
 * ────────────────
 * Encapsulates the cancel-with-reason modal pattern used across all accounting pages
 * (Pledges, Receipts, PaymentVouchers).
 *
 * @param {Function} cancelFn   Async (id, reason) → cancels the item via API
 * @param {Function} onSuccess  Called after successful cancel (e.g. reload())
 * @param {string}   successMsg Toast message on success
 *
 * @returns {{ cancelModal, cancelReason, openCancel, closeCancel, setReason, handleCancel }}
 *
 * Usage:
 *   const { cancelModal, cancelReason, openCancel, closeCancel, setReason, handleCancel } =
 *     useCancelConfirm(
 *       (id, reason) => cancelPledge(id, reason),
 *       reload,
 *       TOAST.cancelSuccess(ENTITIES.pledge)
 *     );
 */
export function useCancelConfirm(cancelFn, onSuccess, successMsg, { requireReason = true } = {}) {
  const [cancelModal, setCancelModal] = useState(null); // the item being cancelled
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const openCancel = (item) => {
    setCancelModal(item);
    setCancelReason('');
  };

  const closeCancel = () => {
    setCancelModal(null);
    setCancelReason('');
  };

  const handleCancel = async () => {
    if (requireReason && !cancelReason.trim()) {
      toast.error(TOAST.reasonRequired);
      return;
    }
    setCancelling(true);
    try {
      await cancelFn(cancelModal.id, requireReason ? cancelReason.trim() : undefined);
      toast.success(successMsg || 'Cancelled successfully');
      closeCancel();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || TOAST.actionFailed('cancel'));
    } finally {
      setCancelling(false);
    }
  };

  return {
    cancelModal,
    cancelReason,
    cancelling,
    openCancel,
    closeCancel,
    setReason: setCancelReason,
    handleCancel,
  };
}
